import { ButtonType, ButtonWidget, InteractionState } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';

import { AppState } from '~src/app/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { IEntityAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { RenameGroupAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/RenameEntityAction';
import { ConnectedSpaceHeader, SpaceHeaderTestIds } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';

import { MapDispatchToProps, MapStateToProps } from '../../../../app/utils/ConnectUtils';
import { imgPath } from '../../../../common/utilities/ImgUrl';
import { IGroupInfo } from '../../../../ts/models/IGroupInfo';
import { ISpaceItemDetailInfo } from '../../../../ts/models/ISpaceItemDetailInfo';
import { SyncActiveDirectoryGroupAction } from '../../../../userManagement/groups/entityActions/SyncActiveDirectoryGroupEntityAction';

export interface GroupSpaceHeaderProps {
  readonly item: ISpaceItemDetailInfo<'group'>;
}

export function GroupSpaceHeader(props: GroupSpaceHeaderProps): JSX.Element {
  return <ConnectedSpaceHeader
      item={props.item}
      nameRowIcons={
        <React.Fragment>
          { RenameButton(props.item) }
          { SyncGroupButton(props.item) }
        </React.Fragment>
      }
      metadataRow={
        <React.Fragment>
          <MetaDataRow {...props} />
        </React.Fragment>
      }
  />;
}

export function MetaDataRow(props: GroupSpaceHeaderProps) {
  return (
    <>
      <span className={SpaceHeaderStyles.GroupLabel}>
        {Messages.group()}
      </span>
      { props.item.domainName &&
        <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.domain}>
          {Messages.newDomainLabel({DOMAIN_LABEL: props.item.domainName})}
        </span>
      }
    </>
  );
}

interface NameRowButtonOwnProps {
  readonly iconPath: string;
  readonly iconActivePath: string;
  readonly action: IEntityAction<IGroupInfo, {}>;
  readonly item: ISpaceItemDetailInfo<'group'>;
}

interface NameRowButtonStateProps {
  readonly isVisible: boolean;
}

interface NameRowButtonDispatchProps {
  readonly onClick: () => void;
}

type NameRowButtonProps = NameRowButtonOwnProps & NameRowButtonStateProps & NameRowButtonDispatchProps;

function NameRowButton(props: NameRowButtonProps) {
  if (!props.isVisible) {
    return null;
  }

  return (
    <ButtonWidget
      buttonType={ButtonType.LowEmphasis}
      getContent={getDetailButtonContent(props.iconPath, props.iconActivePath)}
      title={props.action.translateName()}
      testId={props.action.name}
      handleClick={props.onClick}
      style={SpaceHeaderStyles.IconButtonStyleOverrides}
    />
  );
}

const getDetailButtonContent = (iconPath: string, activePath: string): (state: InteractionState) => JSX.Element => {
  return (state: InteractionState): JSX.Element => {
    const showActiveIcon = state.hover || state.active || state.focus;
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <img
        className={SpaceHeaderStyles.HeaderButtonImage}
        src={showActiveIcon ? activePath : iconPath }
      />
    );
  };
};

const mapStateToProps: MapStateToProps<NameRowButtonStateProps> = (state: AppState, ownProps: NameRowButtonOwnProps) => ({
  isVisible: ownProps.action.isClickable({}, [ownProps.item], state) && ownProps.action.isVisible([ownProps.item], state)
});

const mapDispatchToProps: MapDispatchToProps<NameRowButtonDispatchProps> = (dispatch, ownProps: NameRowButtonOwnProps) => {
  return {
    onClick: () => {
      dispatch(ownProps.action.createReduxAction([ownProps.item]));
    }
  };
};

export const ConnectedNameRowButton: React.ComponentClass<NameRowButtonOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(NameRowButton);

const RenameButton = (item: ISpaceItemDetailInfo<'group'>) => {
  const ICON_PATH = imgPath('rename_normal.svg');
  const ACTIVE_ICON_PATH = imgPath('rename_hover.svg');

  return <ConnectedNameRowButton action={RenameGroupAction} iconPath={ICON_PATH} iconActivePath={ACTIVE_ICON_PATH} item={item}/>;
};

const SyncGroupButton = (item: ISpaceItemDetailInfo<'group'>) => {
  const ICON_PATH = imgPath('refresh_normal.svg');
  const ACTIVE_ICON_PATH = imgPath('refresh_hover.svg');

  return <ConnectedNameRowButton action={SyncActiveDirectoryGroupAction} iconPath={ICON_PATH} iconActivePath={ACTIVE_ICON_PATH} item={item}/>;
};
